import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherRotateCcw, FeatherSearch } from "@subframe/core";

import type { CategoryResponseDto } from "@/generated/model/categoryResponseDto";
import { FollowUpActionResponseDtoDueStatus } from "@/generated/model/followUpActionResponseDtoDueStatus";
import type { NurseryItemDto } from "@/generated/model/nurseryItemDto";
import type { NurseryMemberDto } from "@/generated/model/nurseryMemberDto";
import { NurseryMemberDtoRole } from "@/generated/model/nurseryMemberDtoRole";
import {
  ALL_FILTER_VALUE,
  DUE_STATUS_OPTIONS,
  encodeAssigneeFilter,
  roleFilterLabel,
  UNCATEGORISED_CATEGORY_ID,
} from "@/lib/task-manager";

/** Roles an action can be assigned to, offered as "Any <role>" options. */
const ASSIGNABLE_ROLES: NurseryMemberDtoRole[] = [
  NurseryMemberDtoRole.MANAGER,
  NurseryMemberDtoRole.DEPUTY_MANAGER,
  NurseryMemberDtoRole.LEADER,
  NurseryMemberDtoRole.PRACTITIONER,
];

interface TaskManagerFiltersProps {
  categories: CategoryResponseDto[];
  categoryId?: string;
  onCategoryChange: (categoryId: string | undefined) => void;

  /** Nurseries the user can access; the control is hidden when there is only one. */
  nurseries: NurseryItemDto[];
  selectedNurseryId: string | null;
  onNurseryChange: (nurseryId: string) => void;

  members: NurseryMemberDto[];
  assignee?: string;
  onAssigneeChange: (assignee: string | undefined) => void;

  /** Hidden on tabs whose rows carry no due bucket. */
  showDueStatus: boolean;
  dueStatus?: FollowUpActionResponseDtoDueStatus;
  onDueStatusChange: (
    dueStatus: FollowUpActionResponseDtoDueStatus | undefined,
  ) => void;

  search: string;
  onSearchChange: (search: string) => void;

  canReset: boolean;
  onReset: () => void;
}

/**
 * The Task Manager filter row: Category, Location, Assigned to, Status, search
 * and Reset.
 *
 * "Location" selects the nursery, which is the page's scope rather than a filter
 * — picking one writes the session-wide selection that the top bar switcher also
 * writes. That is why Reset deliberately leaves it alone.
 */
export function TaskManagerFilters({
  categories,
  categoryId,
  onCategoryChange,
  nurseries,
  selectedNurseryId,
  onNurseryChange,
  members,
  assignee,
  onAssigneeChange,
  showDueStatus,
  dueStatus,
  onDueStatusChange,
  search,
  onSearchChange,
  canReset,
  onReset,
}: TaskManagerFiltersProps) {
  const showLocation = nurseries.length > 1;

  return (
    <div className="flex w-full flex-wrap items-center gap-3">
      <div data-testid="task-filter-category">
        <Select
          placeholder="All categories"
          value={categoryId ?? ALL_FILTER_VALUE}
          onValueChange={(value) =>
            onCategoryChange(value === ALL_FILTER_VALUE ? undefined : value)
          }
        >
          <Select.Item value={ALL_FILTER_VALUE}>All categories</Select.Item>
          {categories.map((category) => (
            <Select.Item key={category.id} value={category.id}>
              {category.name}
            </Select.Item>
          ))}
          {/* Most actions carry no category, so without this they would be
              unreachable the moment a category is picked. */}
          <Select.Item value={UNCATEGORISED_CATEGORY_ID}>
            Uncategorised
          </Select.Item>
        </Select>
      </div>

      {showLocation && (
        <div data-testid="task-filter-location">
          <Select
            placeholder="All locations"
            value={selectedNurseryId ?? undefined}
            onValueChange={onNurseryChange}
          >
            {nurseries.map((nursery) => (
              <Select.Item key={nursery.id} value={nursery.id}>
                {nursery.name}
              </Select.Item>
            ))}
          </Select>
        </div>
      )}

      <div data-testid="task-filter-assignee">
        <Select
          placeholder="All assignees"
          value={assignee ?? ALL_FILTER_VALUE}
          onValueChange={(value) =>
            onAssigneeChange(value === ALL_FILTER_VALUE ? undefined : value)
          }
        >
          <Select.Item value={ALL_FILTER_VALUE}>All assignees</Select.Item>
          {ASSIGNABLE_ROLES.map((role) => (
            <Select.Item
              key={role}
              value={encodeAssigneeFilter({ kind: "role", role })}
            >
              {roleFilterLabel(role)}
            </Select.Item>
          ))}
          {members.map((member) => (
            <Select.Item
              key={member.userId}
              value={encodeAssigneeFilter({
                kind: "user",
                userId: member.userId,
              })}
            >
              {`${member.firstName ?? ""} ${member.lastName ?? ""}`.trim() ||
                member.email}
            </Select.Item>
          ))}
        </Select>
      </div>

      {showDueStatus && (
        <div data-testid="task-filter-status">
          <Select
            placeholder="All statuses"
            value={dueStatus ?? ALL_FILTER_VALUE}
            onValueChange={(value) =>
              onDueStatusChange(
                value === ALL_FILTER_VALUE
                  ? undefined
                  : (value as FollowUpActionResponseDtoDueStatus),
              )
            }
          >
            <Select.Item value={ALL_FILTER_VALUE}>All statuses</Select.Item>
            {DUE_STATUS_OPTIONS.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select>
        </div>
      )}

      <Button
        variant="neutral-tertiary"
        icon={<FeatherRotateCcw />}
        disabled={!canReset}
        onClick={onReset}
      >
        Reset filters
      </Button>

      <div className="grow" />

      <TextField
        className="h-auto w-full lg:w-72"
        variant="outline"
        size="base"
        icon={<FeatherSearch />}
      >
        <TextField.Input
          placeholder="Search tasks"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </TextField>
    </div>
  );
}
